import { Button, formatDate, Spinner, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import type { SupportTicketDto, TicketPriority } from './contractsMirror.js';
import {
  useSupportMessagesQuery,
  useSupportMutation,
  useSupportQuery,
  useSupportTicketSocket,
} from './useSupportQuery.js';

/**
 * The redesigned Support screen (UI-SPEC archetype F/E): a submit form + (when present) the
 * tenant's ticket history queue, with a success status announced on submit (the "toast"
 * affordance — `@hbs-us/ui` ships no Toast primitive, so a `role="status"` live region is the
 * accessible equivalent the codebase uses, e.g. MyAccountRoute).
 *
 * The list comes from the coarse `GET /api/v1/support/tickets`; the submit posts to
 * `POST /api/v1/support`. The SPA never sends a tenant id — the server resolves the principal
 * (SEC-11). Every color is a locked token (BRAND-06).
 */

const SECTION = 'rounded-xl border border-border bg-card p-6 shadow-sm';
const FIELD_LABEL = 'font-medium text-foreground text-sm';
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-card px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA =
  'mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const PRIORITIES: readonly TicketPriority[] = ['low', 'normal', 'high', 'critical'] as const;

function TicketRow({
  ticket,
  active,
  onSelect,
}: {
  ticket: SupportTicketDto;
  active: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={
          active
            ? 'w-full rounded-xl border border-muted-foreground bg-card p-4 text-left text-foreground shadow-sm'
            : 'w-full rounded-xl border border-border bg-background p-4 text-left text-foreground transition-colors hover:bg-sidebar-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-sm">{ticket.subject}</span>
          <span className="rounded-full border border-border bg-card px-2 py-0.5 font-medium text-muted-foreground text-xs">
            {t(`support:status.${ticket.status}`, { defaultValue: ticket.status })}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-muted-foreground text-sm">{ticket.message}</p>
        <p className="mt-2 text-muted-foreground text-xs">
          {formatDate(ticket.createdAt, locale, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      </button>
    </li>
  );
}

function SupportForm({ onCreated }: { onCreated: (ticketId: number) => void }) {
  const { t } = useTranslation();
  const mutation = useSupportMutation();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    mutation.mutate(
      { subject, message, priority },
      {
        onSuccess: (data) => {
          setSubject('');
          setMessage('');
          setPriority('normal');
          if (data.ticket?.id) onCreated(data.ticket.id);
        },
      },
    );
  }

  return (
    <form className="rounded-xl border border-border bg-card p-5 shadow-sm" onSubmit={onSubmit}>
      <h2 className="mb-2 font-display font-semibold text-foreground text-lg">
        {t('support:form.heading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">{t('support:form.body')}</p>
      <div className="grid gap-4">
        <div>
          <label htmlFor="support-subject" className={FIELD_LABEL}>
            {t('support:form.subject')}
          </label>
          <input
            id="support-subject"
            className={INPUT}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={140}
          />
        </div>
        <div>
          <label htmlFor="support-priority" className={FIELD_LABEL}>
            {t('support:form.priority')}
          </label>
          <select
            id="support-priority"
            className={INPUT}
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`support:priority.${p}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="support-message" className={FIELD_LABEL}>
            {t('support:form.message')}
          </label>
          <textarea
            id="support-message"
            rows={5}
            className={TEXTAREA}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
          />
        </div>

        {mutation.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {t('support:form.error')}
          </p>
        ) : null}
        {mutation.isSuccess ? (
          <p role="status" className="text-secondary-foreground text-sm">
            {t('support:form.success')}
          </p>
        ) : null}

        <div className="flex">
          <Button type="submit" disabled={mutation.isPending || !subject.trim() || !message.trim()}>
            {t('support:form.submit')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function SupportChat({ ticket }: { ticket: SupportTicketDto }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const query = useSupportMessagesQuery(ticket.id);
  const socket = useSupportTicketSocket(ticket.id);
  const [body, setBody] = useState('');

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const text = body.trim();
    if (!text) return;
    if (socket.sendMessage(text)) setBody('');
  }

  const messages = query.data?.messages ?? [];

  return (
    <section className="flex min-h-[34rem] flex-col rounded-xl border border-border bg-card shadow-sm">
      <div className="border-border border-b p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display font-semibold text-foreground text-xl">
              {t('support:chat.heading')}
            </h2>
            <p className="mt-1 text-muted-foreground text-sm">{ticket.subject}</p>
          </div>
          <span className="rounded-full border border-border bg-background px-3 py-1 text-muted-foreground text-xs">
            {socket.isOpen ? t('support:chat.live') : t('support:chat.connecting')}
          </span>
        </div>
      </div>

      {query.isPending ? (
        <div className="flex flex-1 justify-center py-8">
          <Spinner label={t('common:status.loading')} />
        </div>
      ) : (
        <ol className="flex-1 space-y-3 overflow-auto p-5">
          {messages.map((message) => {
            const fromPlatform = message.senderType === 'platform';
            return (
              <li
                key={message.id}
                className={fromPlatform ? 'flex justify-start' : 'flex justify-end'}
              >
                <article
                  className={
                    fromPlatform
                      ? 'max-w-[85%] rounded-2xl border border-border bg-background px-4 py-3 text-foreground'
                      : 'max-w-[85%] rounded-2xl border border-border bg-card px-4 py-3 text-foreground shadow-sm'
                  }
                >
                  <p className="font-medium text-xs">
                    {fromPlatform ? t('support:chat.platformSender') : message.senderName}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{message.body}</p>
                  <p
                    className={
                      fromPlatform
                        ? 'mt-2 font-mono text-muted-foreground text-xs'
                        : 'mt-2 font-mono text-muted-foreground text-xs'
                    }
                  >
                    {formatDate(message.createdAt, locale, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </p>
                </article>
              </li>
            );
          })}
        </ol>
      )}

      <form className="border-border border-t bg-card p-5" onSubmit={onSubmit}>
        <label htmlFor="support-chat-message" className={FIELD_LABEL}>
          {t('support:chat.replyLabel')}
        </label>
        <textarea
          id="support-chat-message"
          rows={3}
          maxLength={5000}
          className={TEXTAREA}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {socket.error ? (
          <p role="alert" className="text-destructive text-sm">
            {t('support:chat.error')}
          </p>
        ) : null}
        <div className="mt-3 flex justify-end">
          <Button type="submit" disabled={!socket.isOpen || !body.trim()}>
            {t('support:chat.send')}
          </Button>
        </div>
      </form>
    </section>
  );
}

export function SupportRoute() {
  const { t } = useTranslation();
  const query = useSupportQuery();
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [showNewRequest, setShowNewRequest] = useState(false);

  const tickets = query.data?.tickets ?? [];
  const selectedTicket = tickets.find((ticket) => ticket.id === selectedTicketId) ?? null;

  useEffect(() => {
    if (selectedTicketId === null && tickets.length > 0) {
      setSelectedTicketId(tickets[0].id);
    }
  }, [selectedTicketId, tickets]);

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('support:title')}
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('support:subtitle')}</p>
      </header>

      <div className="grid gap-6 xl:grid-cols-[24rem_minmax(0,1fr)]">
        <div className="space-y-4">
          <section className={SECTION}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display font-semibold text-foreground text-lg">
                  {t('support:history.heading')}
                </h2>
                <p className="mt-1 text-muted-foreground text-sm">{t('support:history.body')}</p>
              </div>
              <Button
                type="button"
                variant={showNewRequest ? 'ghost' : 'secondary'}
                size="sm"
                onClick={() => setShowNewRequest((value) => !value)}
              >
                {showNewRequest ? t('support:form.cancel') : t('support:form.newRequest')}
              </Button>
            </div>
            {query.isPending ? (
              <div className="flex justify-center py-8">
                <Spinner label={t('common:status.loading')} />
              </div>
            ) : tickets.length > 0 ? (
              <ul className="space-y-3">
                {tickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    active={ticket.id === selectedTicketId}
                    onSelect={() => setSelectedTicketId(ticket.id)}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">{t('support:history.empty')}</p>
            )}
          </section>

          {showNewRequest ? (
            <SupportForm
              onCreated={(ticketId) => {
                setSelectedTicketId(ticketId);
                setShowNewRequest(false);
              }}
            />
          ) : null}
        </div>

        {selectedTicket ? (
          <SupportChat ticket={selectedTicket} />
        ) : (
          <section className={SECTION}>
            <h2 className="font-display font-semibold text-foreground text-xl">
              {t('support:chat.emptyHeading')}
            </h2>
            <p className="mt-2 text-muted-foreground text-sm">{t('support:chat.emptyBody')}</p>
          </section>
        )}
      </div>
    </div>
  );
}
